
import { defineComponent } from "vue";
import { useRoute } from "vue-router";
import {
  IStudentsModel,
  IAcademicYearModel,
  ITuitionPriceLinesModel,
  ITuitionPriceListsModel,
  ITuitionDiscountsModel,
  IIndividualEnrollmentModel,
  ICourseGroupsModel,
  IHeadquartersModel,
  ICoursesModel
} from "@/interfaces/modules/";

import StudenService from "@/api/Aula/StudentsService";
import AcademicYearService from "@/api/Configuration/AcademicYearService";
import CourseGroupsService from "@/api/Aula/CourseGroupsService";
import HeadquartersService from "@/api/Configuration/HeadquartersService";
import CoursesService from "@/api/Aula/CoursesSevice";
import IndividualEnrollmentService from "@/api/Enrollment/IndividualEnrollmentService";
import TuitionDiscountsService from "@/api/Collection/TuitionDiscountsService";
import TuitionPriceListsService from "@/api/Collection/TuitionPriceListsService";
import TuitionPriceLinesService from "@/api/Collection/TuitionPriceLinesService";
import store from "@/store";
import router from "@/router";
import { SHOW_PRESCHOOL } from "@/enums";

export default defineComponent({
  name: "EnrollementIndividual",
  data() {
    const route = useRoute();
    return {
      enrollmentId: route.params.Id || 0,
      disabledRecord: false,

      students: [] as IStudentsModel[],
      academicYears: [] as IAcademicYearModel[],
      coursesGroup: [] as ICourseGroupsModel[],
      coursesGroupAvailable: [] as ICourseGroupsModel[],
      headquarters: [] as IHeadquartersModel[],
      courses: [] as ICoursesModel[],
      priceLines: [] as ITuitionPriceLinesModel[],
      priceList: [] as ITuitionPriceListsModel[],
      discounts: [] as ITuitionDiscountsModel[],

      priceLineFiltered: [] as ITuitionPriceLinesModel[],
      priceListRecord: {} as ITuitionPriceListsModel,
      priceRecord: {} as ITuitionPriceLinesModel,
      discountRecord: {} as ITuitionDiscountsModel,
      studentRecord: {} as IStudentsModel,

      paymentError: "",

      // ✅ NEW: docs section state
      selectedReport: "CONFIRMED_ENROLLMENT",
      printing: false,

      form: {
        loading: true,
        dialog: false,
        valid: false,
        wasValidated: false,
        record: {} as IIndividualEnrollmentModel,
        rules: {
          required: [(v: any) => !!v || "Este campo es requerido"],
        },
      }
    };
  },

  watch: {
    'form.record.StudentId'(val: any) {
      this.form.wasValidated = !val || !this.form.record.StudentId;
    },
    'form.record.AcademicYearId'(val: any) {
      this.form.wasValidated = !val || !this.form.record.AcademicYearId;
      this.changeValuesLoad();
    },
    'form.record.HeadquarterId'(val: any) {
      this.form.wasValidated = !val || !this.form.record.HeadquarterId;
      if (!val) this.form.record.CourseGroupId = 0 as any;
      this.changeValuesLoad();
    },
    'form.record.CourseGroupId'(val: any) {
      this.form.wasValidated = !val || !this.form.record.CourseGroupId;
    },
    'form.record.MethodOfPayment'(val: string | undefined, oldVal: string | undefined) {
      if (!val && oldVal) {
        this.$nextTick(() => (this.form.record.MethodOfPayment = oldVal));
      }
    },

    // keep string in sync whenever array changes
    'form.record.TuitionPriceLineIdOthersList': {
      deep: true,
      handler(val: any) {
        (this.form.record as any).TuitionPriceLineIdOthers = this.joinOthersFromList(val);
      }
    }
  },

  computed: {
    canPrint(): boolean {
      return this.toNum((this.form.record as any)?.Id) > 0;
    },

    currency(): string {
      return this.priceListRecord?.Currency || '';
    },

    discountPercent(): number {
      return Number(this.discountRecord?.Value || 0);
    },

    summaryLines(): Array<{ Id: number; Title: string; Amount: number }> {
      const selectedMainId = this.toNum((this.form.record as any)?.TuitionPriceLineId || 0);

      const othersRaw = ((this.form.record as any)?.TuitionPriceLineIdOthersList || []) as any[];
      const otherIds = (Array.isArray(othersRaw) ? othersRaw : [])
        .map(x => this.toNum(x))
        .filter(x => x > 0);

      const ids = [
        ...(selectedMainId ? [selectedMainId] : []),
        ...otherIds
      ];

      const uniqueIds = Array.from(new Set(ids));

      return uniqueIds
        .map(id =>
          this.priceLineFiltered.find(pl => this.toNum(pl.Id) === id) ||
          this.priceLines.find(pl => this.toNum(pl.Id) === id)
        )
        .filter(Boolean)
        .map((pl: any) => {
          const priceList = this.getPriceListById(pl.PriceListId);
          const listName = priceList?.Name ? `${priceList.Name} - ` : '';
          return {
            Id: this.toNum(pl.Id),
            Title: `${listName}${pl.Concept || 'Concepto'}`,
            Amount: Number(pl.Amount || 0),
          };
        });
    },

    subtotal(): number {
      return this.summaryLines.reduce((sum, ln) => sum + (Number(ln.Amount) || 0), 0);
    },

    discountAmount(): number {
      const pct = (Number(this.discountPercent || 0) / 100);
      return +(this.subtotal * pct);
    },

    total(): number {
      return +(this.subtotal - this.discountAmount);
    },
  },

  async mounted() {
    await this.load();

    if (parseInt(this.enrollmentId as any) > 0) {
      this.disabledRecord = true;
      this.form.record = await IndividualEnrollmentService.GetEnrollmentById({ Id: this.enrollmentId });

      // normalize record values (IDs)
      this.hydrateRecordFromApi();

      // load dependent lists AFTER record is normalized
      await this.$nextTick();
      await this.changeValuesLoad();

      // ensure selected values exist in options
      this.ensureSelectedOptionsExist();
    } else {
      this.form.record = {
        Id: 0,
        StudentId: 0,
        AcademicYearId: 0,
        HeadquarterId: 0,
        CourseGroupId: 0,
        TuitionPriceLineId: 0,
        DiscountId: 0,
        TuitionPriceLineIdOthersList: []
      } as any;
    }
  },

  methods: {
    // universal numeric cast
    toNum(v: any): number {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },

    // thousands separator (Colombia)
    formatMoney(value: any): string {
      const n = Number(value || 0);
      return new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    },

    // Parse string -> array
    parseOthersToList(value: any): number[] {
      if (!value) return [];
      const raw = String(value).trim();
      if (!raw) return [];
      const parts = raw.split(/[,\s;|]+/g);
      return Array.from(
        new Set(
          parts
            .map(p => this.toNum(String(p).trim()))
            .filter(n => n > 0)
        )
      );
    },

    // Join array -> string
    joinOthersFromList(list: any): string {
      const arr = Array.isArray(list) ? list : [];
      const ids = Array.from(new Set(arr.map((x: any) => this.toNum(x)).filter((n: number) => n > 0)));
      return ids.join(",");
    },

    // Normalize ALL fetched lists
    normalizeCollections() {
      this.students = (this.students || []).map(s => ({ ...s, Id: this.toNum((s as any).Id) })) as any;
      this.academicYears = (this.academicYears || []).map(y => ({ ...y, Id: this.toNum((y as any).Id) })) as any;
      this.headquarters = (this.headquarters || []).map(h => ({ ...h, Id: this.toNum((h as any).Id) })) as any;

      this.courses = (this.courses || []).map(c => ({
        ...c,
        Id: this.toNum((c as any).Id),
        HeadquarterId: this.toNum((c as any).HeadquarterId)
      })) as any;

      this.coursesGroup = (this.coursesGroup || []).map(cg => ({
        ...cg,
        Id: this.toNum((cg as any).Id),
        AcademicYearId: this.toNum((cg as any).AcademicYearId),
        CourseId: this.toNum((cg as any).CourseId)
      })) as any;

      this.priceList = (this.priceList || []).map(pl => ({
        ...pl,
        Id: this.toNum((pl as any).Id),
        AcademicYearId: this.toNum((pl as any).AcademicYearId)
      })) as any;

      this.priceLines = (this.priceLines || []).map(ln => ({
        ...ln,
        Id: this.toNum((ln as any).Id),
        PriceListId: this.toNum((ln as any).PriceListId),
        Amount: Number((ln as any).Amount || 0)
      })) as any;

      this.discounts = (this.discounts || []).map(d => ({ ...d, Id: this.toNum((d as any).Id) })) as any;
    },

    hydrateRecordFromApi() {
      const r: any = this.form.record as any;

      r.StudentId = this.toNum(r.StudentId);
      r.AcademicYearId = this.toNum(r.AcademicYearId);
      r.HeadquarterId = this.toNum(r.HeadquarterId);
      r.CourseGroupId = this.toNum(r.CourseGroupId);
      r.TuitionPriceLineId = this.toNum(r.TuitionPriceLineId);
      r.DiscountId = this.toNum(r.DiscountId);

      r.EnrollmentDate = String(r.EnrollmentDate || '').substring(0, 10);

      // others: string -> array
      r.TuitionPriceLineIdOthersList = this.parseOthersToList(r.TuitionPriceLineIdOthers);
    },

    ensureSelectedOptionsExist() {
      const r: any = this.form.record as any;

      const selCgId = this.toNum(r.CourseGroupId);
      if (selCgId > 0 && !this.coursesGroupAvailable.some(cg => this.toNum((cg as any).Id) === selCgId)) {
        const cg = this.coursesGroup.find(x => this.toNum((x as any).Id) === selCgId);
        if (cg) {
          this.coursesGroupAvailable = [...this.coursesGroupAvailable, cg].sort((a, b) => ((a as any).Name || '').localeCompare((b as any).Name || ''));
        }
      }

      const selLineId = this.toNum(r.TuitionPriceLineId);
      if (selLineId > 0 && !this.priceLineFiltered.some(pl => this.toNum((pl as any).Id) === selLineId)) {
        const ln = this.priceLines.find(x => this.toNum((x as any).Id) === selLineId);
        if (ln) {
          this.priceLineFiltered = [...this.priceLineFiltered, ln].sort((a, b) => ((a as any).Concept || '').localeCompare((b as any).Concept || ''));
        }
      }
    },

    async load() {
      this.form.loading = true;
      this.students = await StudenService.GetStudents();
      this.academicYears = await AcademicYearService.GetAcademicYears();
      this.coursesGroup = await CourseGroupsService.GetCourseGroups();
      this.headquarters = await HeadquartersService.GetHeadquarters();
      this.courses = await CoursesService.GetCourses(SHOW_PRESCHOOL.No);
      this.priceLines = await TuitionPriceLinesService.GetPriceLines();
      this.priceList = await TuitionPriceListsService.GetPriceLists();
      this.discounts = await TuitionDiscountsService.GetDiscounts();

      this.normalizeCollections();
      this.form.loading = false;
    },

    // NAV HELPERS
    goTo(refName: 'tabStudent' | 'tabPayment' | 'tabConfirm') {
      const r: any = (this.$refs as any)[refName];
      const el: HTMLElement | undefined = r?.$el ? r.$el : r;
      if (el && typeof el.click === 'function') el.click();
    },

    // VALIDATIONS
    validateStudentTab(): boolean {
      this.form.wasValidated = true;
      const r: any = this.form.record || {};
      const missing = [
        this.toNum(r.StudentId),
        this.toNum(r.AcademicYearId),
        this.toNum(r.HeadquarterId),
        this.toNum(r.CourseGroupId),
        r.EnrollmentDate,
        this.toNum(r.TuitionPriceLineId)
      ].some(v => !v || v === 0 || v === '');
      return !missing;
    },

    validatePaymentMethod(): boolean {
      if (!(this.form.record as any).MethodOfPayment) {
        this.paymentError = "Seleccione un método de pago.";
        return false;
      }
      this.paymentError = "";
      return true;
    },

    // UI EVENTS
    setPayment(method: 'TRANSFERENCIA' | 'EFECTIVO') {
      if ((this.form.record as any).MethodOfPayment === method) return;
      (this.form.record as any).MethodOfPayment = method;
      this.paymentError = "";
    },

    getPriceListById(id: any) {
      const _id = this.toNum(id);
      const pl = (this.priceList || []).find(x => this.toNum((x as any).Id) === _id);
      return pl || ({} as ITuitionPriceListsModel);
    },

    async changeValuesLoad() {
      const yearId = this.toNum((this.form.record as any).AcademicYearId);
      const hqId = this.toNum((this.form.record as any).HeadquarterId);
      const studentId = this.toNum((this.form.record as any).StudentId);

      this.studentRecord = this.students.find(s => this.toNum((s as any).Id) === studentId) || ({} as IStudentsModel);

      if (!yearId || yearId <= 0 || !hqId || hqId <= 0) {
        this.coursesGroupAvailable = [];
        this.priceLineFiltered = [];
        return;
      }

      const courseIdsAtHq = new Set(
        (this.courses || [])
          .filter(c => this.toNum((c as any).HeadquarterId) === hqId)
          .map(c => this.toNum((c as any).Id))
      );

      this.coursesGroupAvailable = (this.coursesGroup || [])
        .filter(cg => this.toNum((cg as any).AcademicYearId) === yearId && courseIdsAtHq.has(this.toNum((cg as any).CourseId)))
        .sort((a, b) => ((a as any).Name || '').localeCompare((b as any).Name || ''));

      const priceListsEnabled = (this.priceList ?? [])
        .filter(pl => !!(pl as any).Enable && this.toNum((pl as any).AcademicYearId) === yearId)
        .sort((a, b) => ((a as any).Name || '').localeCompare((b as any).Name || ''));

      const enabledPriceListIds = new Set(priceListsEnabled.map(pl => this.toNum((pl as any).Id)));

      const priceLinesEnabled = (this.priceLines ?? [])
        .filter(line => enabledPriceListIds.has(this.toNum((line as any).PriceListId)))
        .sort((a, b) => ((a as any).Concept || '').localeCompare((b as any).Concept || ''));

      this.priceLineFiltered = priceLinesEnabled;

      const selectedLineId = this.toNum((this.form.record as any).TuitionPriceLineId);
      const selectedLine = priceLinesEnabled.find(l => this.toNum((l as any).Id) === selectedLineId);

      const selectedDiscountId = this.toNum((this.form.record as any).DiscountId);
      this.discountRecord = this.discounts.find(d => this.toNum((d as any).Id) === selectedDiscountId) || ({} as ITuitionDiscountsModel);

      if (selectedLine) {
        this.priceRecord = selectedLine as any;
        this.priceListRecord =
          (priceListsEnabled.find(pl => this.toNum((pl as any).Id) === this.toNum((selectedLine as any).PriceListId)) as any) ||
          ({} as ITuitionPriceListsModel);
      } else {
        this.priceRecord = {} as ITuitionPriceLinesModel;
        this.priceListRecord = {} as ITuitionPriceListsModel;
      }

      (this.form.record as any).TuitionPriceLineIdOthers =
        this.joinOthersFromList((this.form.record as any).TuitionPriceLineIdOthersList);

      this.ensureSelectedOptionsExist();
    },

    // Submit from first tab
    async handleSubmit(e: Event) {
      e.preventDefault();
      if (this.validateStudentTab()) {
        this.goTo('tabPayment');
      }
    },

    handleNextFromPayment() {
      if (this.validatePaymentMethod()) {
        this.goTo('tabConfirm');
      }
    },

    async registerAdmission() {
      if (!this.validateStudentTab()) {
        this.goTo('tabStudent');
        return;
      }
      if (!this.validatePaymentMethod()) {
        this.goTo('tabPayment');
        return;
      }

      try {
        this.form.loading = true;

        (this.form.record as any).TuitionPriceLineIdOthers =
          this.joinOthersFromList((this.form.record as any).TuitionPriceLineIdOthersList);

        const res = await IndividualEnrollmentService.PostEnrollment(this.form.record);

        store.dispatch("core/popupDialog", {
          text: "Registro completado con éxito.",
          type: "success",
          showCancelButton: false,
        });

        this.form.loading = false;
        // router.push(`/enrollment/enrollment-individual/${(res as any).Id}`);
        await router.push({
          name: "EnrollmentIndividual",
          params: { Id: parseInt((res as any).Id) },
        });
        location.reload();

      } catch (err: any) {
        this.form.loading = false;
        store.dispatch("core/popupDialog", {
          text: `Error: ${err?.response?.data?.message || err?.message}`,
          type: "error",
          showCancelButton: false,
        });
      }
    },

    // ✅ PRINT REPORT (replace implementation with your real API endpoint)
    async printReport(type: string) {
      if (!this.canPrint) {
        store.dispatch("core/popupDialog", {
          text: "Debe guardar la matrícula antes de imprimir.",
          type: "warning",
          showCancelButton: false,
        });
        return;
      }

      try {
        this.printing = true;
        const id = this.toNum((this.form.record as any).Id);
        let res: any = {};
        if (type === 'CONFIRMED_ENROLLMENT') {
          res = await IndividualEnrollmentService.GetPrintEnrollment(id);
        } else if (type === 'GOVERNMENT_CONFIRMATION') {
          // Call API to generate government confirmation report
          // Example: await IndividualEnrollmentService.GenerateGovernmentConfirmationReport(id);
        } else if (type === 'ENROLLMENT_CERTIFICATION') {
          // Call API to generate enrollment certification report
          // Example: await IndividualEnrollmentService.GenerateEnrollmentCertificationReport(id);
        }

        store.dispatch("core/popupDialog", {
          text: "Archivo generado con exito.",
          type: "success",
          showCancelButton: false,
        });
        window.open(res.pathHost, '_blank')
      } catch (err: any) {
        store.dispatch("core/popupDialog", {
          text: `Error al imprimir: ${err?.response?.data?.message || err?.message}`,
          type: "error",
          showCancelButton: false,
        });
      } finally {
        this.printing = false;
      }
    },
  },
});
